import { createContext, useCallback, useContext, useEffect, useState } from "react";
import * as api from "../api/client";

// Mirrors DashboardsContext's shape (createContext -> provider -> hook that throws outside it), but
// holds the logged-in user. The session lives in an HttpOnly cookie the browser sends automatically —
// nothing about it is kept in localStorage — so on load we ask the server who we are via GET /api/me.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // true until the initial /me resolves

  const refresh = useCallback(async () => {
    try {
      setUser(await api.getMe());
    } catch {
      setUser(null); // 401 (not logged in) or transient error -> treat as signed out
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Any 401 from the API layer (session expired, logged out elsewhere, account deleted) drops us back
  // to the login screen without each caller having to handle it.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener("auth-expired", onExpired);
    return () => window.removeEventListener("auth-expired", onExpired);
  }, []);

  const login = useCallback(async (username, password, workspace) => {
    const u = await api.login(username, password, workspace);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      window.location.assign("/login");
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
